import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlarmClock, Bell, CheckCheck, Flame, Gift, MessageSquare, Moon, PartyPopper, Sparkles, Trophy, Users } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { useAppNotifications } from '../contexts/AppNotificationsContext'
import { formatHumanDate } from '../lib/dateUtils'

export default function NotificationsDropdown() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    getNotificationPath
  } = useAppNotifications()

  const [isOpen, setIsOpen] = useState(false)

  // Fetch notifications when dropdown opens
  const handleOpenChange = (open) => {
    setIsOpen(open)
    if (open) {
      fetchNotifications(20)
    }
  }

  const getIcon = (type) => {
    switch (type) {
      case 'bracket_official_updated':
        return <Trophy className="h-4 w-4 text-[var(--teal-9)]" />
      case 'prize_awarded':
        return <Gift className="h-4 w-4 text-[var(--orange-9)]" />
      case 'group_invite':
        return <Users className="h-4 w-4 text-primary" />
      case 'new_tournament':
        return <Sparkles className="h-4 w-4 text-[var(--teal-9)]" />
      case 'picks_open':
        return <Trophy className="h-4 w-4 text-[var(--blue-9)]" />
      case 'lock_reminder':
        return <AlarmClock className="h-4 w-4 text-[var(--orange-9)]" />
      case 'streak_milestone':
        return <Flame className="h-4 w-4 text-[var(--orange-9)]" />
      case 'group_message':
        return <MessageSquare className="h-4 w-4 text-primary" />
      case 'group_kudos':
        return <PartyPopper className="h-4 w-4 text-[var(--magenta-9)]" />
      case 'group_quiet':
        return <Moon className="h-4 w-4 text-muted-foreground" />
      default:
        return <Bell className="h-4 w-4 text-primary" />
    }
  }

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatHumanDate(date)
  }

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }

    // Navigate to the appropriate page
    const path = getNotificationPath(notification)
    setIsOpen(false)
    navigate(path)
  }

  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation()
    await markAllAsRead()
    setIsOpen(false)
  }


  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full transition-[background-color,box-shadow,transform,color] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)] hover:text-[var(--color-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[600px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {notifications.length > 0 && unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-3 py-1.5 bg-[var(--color-royal-purple)] hover:bg-[var(--color-primary-hover)] text-[var(--color-white)] text-xs font-medium rounded-md transition-colors flex items-center gap-1.5"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all as read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.slice(0, 10).map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5 flex-shrink-0">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="h-2 w-2 bg-primary rounded-full flex-shrink-0 mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {notification.body}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      </DropdownMenuContent>
    </DropdownMenu>
  )
}
